/**
 * Every German string the UI renders.
 *
 * Code, identifiers and comments are English throughout the project; only the
 * text that reaches the screen is German, and collecting it here keeps that
 * separation enforceable. The wording is taken verbatim from the design
 * reference, including typographic quotation marks, en dashes and middots.
 */

export const de = {
  app: {
    name: 'Betreuung',
  },

  tabs: {
    current: 'Aktuell',
    month: 'Monat',
    year: 'Jahr',
    stats: 'Statistik',
    changes: 'Änderungen',
    admin: 'Verwaltung',
    settings: 'Einstellungen',
  },

  login: {
    title: 'Betreuung',
    subtitle: 'Melde dich an, um den Kalender zu sehen',
    name: 'Name',
    password: 'Passwort',
    submit: 'Anmelden',
    submitting: 'Anmelden …',
    staySignedIn: 'Angemeldet bleiben (dieses Gerät)',
    failed: 'Name oder Passwort falsch',
    rateLimited: 'Zu viele Fehlversuche. Bitte später erneut versuchen.',
  },

  current: {
    runningLabel: 'LÄUFT GERADE',
    until: (value: string) => `bis ${value}`,
    with: (name: string) => `bei ${name}`,
    since: (value: string) => `seit ${value}`,
    next: (name: string) => `danach: ${name}`,
    nothingRunning: 'Gerade nicht zugewiesen',
    openEnded: 'Keine weiteren Zuteilungen eingetragen',
    loadingMore: 'Weitere werden beim Scrollen geladen …',
    empty: 'Keine Zuteilungen vorhanden',
  },

  month: {
    /** Hint line, per selection mode and per pointer type. */
    hintSingleTouch: 'Tag antippen öffnet den Tagesverlauf',
    hintSinglePointer: 'Tag klicken öffnet den Tagesverlauf',
    hintRangeTouch: 'Starttag antippen, dann Endtag — Zeiten kommen im Modal',
    hintRangePointer: 'Starttag klicken, dann Endtag — Zeiten kommen im Modal',
    selectionMode: 'Auswahl',
    modeSingle: 'Einzeltag',
    modeRange: 'Zeitraum',
    today: 'Heute',
    previous: 'Vorheriger Monat',
    next: 'Nächster Monat',
    assign: 'Zuweisen',
    clearSelection: 'Auswahl aufheben',
    weekdays: ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'],
  },

  timeline: {
    grid: '30-Min-Raster',
    hint: 'Zuweisung antippen = bearbeiten · leerer Slot + Slot = neue Zuweisung',
    hintReadOnly: 'Nur Ansicht — der Tagesverlauf kann hier nicht geändert werden',
    assignRange: (range: string) => `${range} zuweisen`,
  },

  year: {
    hint: 'Monat antippen → Monatsansicht · Farbe = überwiegende Betreuung',
    previous: 'Vorheriges Jahr',
    next: 'Nächstes Jahr',
    days: (count: number) => `${count} Tage`,
  },

  stats: {
    modeMonth: 'Monat',
    modeYear: 'Jahr',
    total: (value: string) => `Gesamt: ${value}`,
    averagePerMonth: 'Durchschnitt pro Monat',
    average: (value: string) => `Ø ${value} / Monat`,
    selectPeriod: 'Zeitraum wählen',
    /** The month/year switch carries no visible label in the design. */
    modeSwitch: 'Monat oder Jahr',
  },

  changes: {
    assignmentCreated: 'Zuweisung erstellt',
    assignmentUpdated: 'Zuweisung geändert',
    assignmentDeleted: 'Zuweisung gelöscht',
    personCreated: (name: string) => `Person angelegt — „${name}“`,
    personUpdated: (name: string) => `Person geändert — „${name}“`,
    personDeleted: (name: string) => `Person gelöscht — „${name}“`,
    colorChange: (from: string, to: string) => `Farbe ${from} → ${to}`,
    nameChange: (from: string, to: string) => `Name „${from}“ → „${to}“`,
    accountAdded: 'Account eingerichtet',
    accountRemoved: 'Account entfernt',
    adminGranted: 'Admin-Berechtigung erteilt',
    adminRevoked: 'Admin-Berechtigung entzogen',
    viewOnlyGranted: 'Auf „Nur Ansicht“ gesetzt',
    viewOnlyRevoked: '„Nur Ansicht“ aufgehoben',
    systemActor: 'System',
    loadingMore: 'Ältere werden beim Scrollen geladen …',
    empty: 'Noch keine Änderungen protokolliert',
  },

  modal: {
    createTitle: 'Neue Zuweisung',
    editTitle: 'Zuweisung bearbeiten',
    hint: 'Tag-Auswahl füllt 00:00 – 24:00 vor; Zeiten hier verfeinern',
    from: 'Von',
    to: 'Bis',
    stepperHint: '± springt in 30-Min-Schritten',
    assignTo: 'Zuweisen an',
    unassigned: 'Nicht zugewiesen',
    save: 'Speichern',
    saving: 'Speichern …',
    cancel: 'Abbrechen',
    delete: 'Zuweisung löschen',
    logged: 'Wird im Änderungsprotokoll erfasst',
    invalidRange: 'Ende muss nach dem Beginn liegen',
    decrease: 'Eine halbe Stunde früher',
    increase: 'Eine halbe Stunde später',
  },

  pastDialog: {
    title: 'Vergangenen Zeitraum bearbeiten?',
    body: (range: string, person: string) =>
      `Diese Zuweisung liegt in der Vergangenheit (${range}, ${person}). Bist du sicher? Die Änderung wird protokolliert.`,
    bodyWithoutPerson: (range: string) =>
      `Dieser Zeitraum liegt in der Vergangenheit (${range}). Bist du sicher? Die Änderung wird protokolliert.`,
    cancel: 'Abbrechen',
    confirm: 'Trotzdem bearbeiten',
  },

  conflictDialog: {
    title: 'Nicht gespeichert — Konflikt',
    intro: 'Der Zeitraum überschneidet sich inzwischen mit einer anderen Zuweisung:',
    outro: 'Dein Entwurf wurde verworfen. Bitte bearbeite die bestehende Zuweisung direkt.',
    close: 'Schließen',
    openExisting: 'Bestehende öffnen',
  },

  staleDialog: {
    title: 'Nicht gespeichert — inzwischen geändert',
    intro: 'Diese Zuweisung wurde zwischenzeitlich von jemand anderem geändert:',
    introDeleted: 'Diese Zuweisung wurde zwischenzeitlich gelöscht.',
    outro: 'Dein Entwurf wurde verworfen. Bitte bearbeite den aktuellen Stand.',
    close: 'Schließen',
    openCurrent: 'Aktuelle öffnen',
  },

  admin: {
    back: 'Zurück',
    appearance: 'Darstellung',
    themeSystem: 'System',
    themeLight: 'Hell',
    themeDark: 'Dunkel',
    persons: 'Personen',
    addPerson: '+ Person',
    edit: 'Bearbeiten',
    delete: 'Löschen',
    /** Row actions repeat per person, so their accessible names carry the name. */
    editPerson: (name: string) => `${name} bearbeiten`,
    deletePerson: (name: string) => `${name} löschen`,
    badgeAccount: 'Account',
    badgeNoAccount: 'Ohne Account',
    badgeAdmin: 'Admin',
    badgeViewOnly: 'Nur Ansicht',
    viewOnlyNotice: 'Nur Ansicht — du kannst den Kalender sehen, aber nichts ändern.',
    assignmentCount: (count: number) =>
      count === 0 ? 'Keine Zuweisungen' : `${count} ${count === 1 ? 'Zuweisung' : 'Zuweisungen'}`,
    deleteFootnote:
      'Personen mit bestehenden Zuweisungen können nicht gelöscht werden. Betreuungszeit kann jede Person mit Account jeder Betreuungsperson zuweisen — außer „Nur Ansicht“: die sieht alles und ändert nichts.',
    signOut: 'Abmelden',
  },

  personSheet: {
    createTitle: 'Person anlegen',
    editTitle: 'Person bearbeiten',
    name: 'Name',
    namePlaceholder: 'Name der Person',
    access: 'Zugang',
    withAccount: 'Mit Account',
    withoutAccount: 'Ohne Account',
    viewOnly: 'Nur Ansicht',
    viewOnlyHint: 'Kann alles sehen, aber nichts ändern — und bekommt keine Betreuungszeit',
    password: 'Passwort',
    passwordPlaceholder: 'Passwort setzen',
    passwordKeep: 'Leer lassen, um das Passwort beizubehalten',
    adminPermission: 'Admin-Berechtigung',
    adminPermissionHint: 'Darf Personen verwalten',
    color: 'Farbe',
    colorHint: 'Kürzel = bereits vergeben (mehrfach möglich, wird aber gewarnt)',
    colorWarning: (names: string) => `Diese Farbe ist bereits vergeben an ${names}.`,
    save: 'Speichern',
    cancel: 'Abbrechen',
    deleteConfirmTitle: 'Person löschen?',
    deleteConfirmBody: (name: string) =>
      `„${name}“ wird dauerhaft gelöscht. Der Vorgang wird protokolliert.`,
    deleteConfirm: 'Löschen',
  },

  errors: {
    generic: 'Etwas ist schiefgelaufen. Bitte erneut versuchen.',
    offline: 'Keine Verbindung zum Server. Änderungen sind gerade nicht möglich.',
    reconnected: 'Verbindung wiederhergestellt.',
    loadFailed: 'Daten konnten nicht geladen werden.',
    retry: 'Erneut versuchen',
    nameTaken: 'Dieser Name ist bereits vergeben.',
    personHasAssignments:
      'Diese Person hat Zuweisungen und kann weder gelöscht noch auf „Nur Ansicht“ gesetzt werden.',
    lastAdmin: 'Die letzte Admin-Berechtigung kann nicht entzogen werden.',
    readOnly: 'Dieser Zugang ist auf „Nur Ansicht“ gesetzt und kann nichts ändern.',
  },

  a11y: {
    openAdmin: 'Verwaltung öffnen',
    closeDialog: 'Schließen',
    selectColor: (name: string) => `Farbe ${name} wählen`,
    dayCell: (date: string, owner: string) => `${date}, ${owner}`,
    unassigned: 'Nicht zugewiesen',
  },
} as const;
